import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ROLES, getRole, getOfferLetterHtml } from '../../lib/onboardingTemplates';
import { reviseOfferLetter, describeChanges, payTypeFor, fmtPay, TRACK_LABEL } from '../../lib/offerRevision';
import { parseLocalDate } from '../../lib/dateUtils';
import { useAuth } from '../../hooks/useAuth';

// Change an offer letter and send it again — "Revise & resend" on a signed
// letter, "Edit & resend" on one still out for signature. The work is in
// lib/offerRevision.js; this is the form and a rendered preview of the letter
// (never the raw HTML — CLAUDE.md, generated documents).
export default function ReviseOfferModal({ staff, signed, onClose, onDone }) {
  const { profile } = useAuth();
  const inWeek = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const [employmentType, setEmploymentType] = useState(['employee', 'contractor'].includes(staff.employment_type) ? staff.employment_type : 'employee');
  const [roleCode, setRoleCode] = useState(staff.hire_role_code || '');
  const [payRate, setPayRate] = useState(staff.pay_rate != null ? String(staff.pay_rate) : '');
  const [startDate, setStartDate] = useState(staff.start_date ? staff.start_date.slice(0, 10) : '');
  const [signBy, setSignBy] = useState(inWeek(7));
  const [reason, setReason] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const roles = ROLES.filter(r => r.offerLetters?.[employmentType]);
  const role = getRole(roleCode);
  const payType = payTypeFor(roleCode);
  const changes = roleCode && payRate ? describeChanges(staff, { roleCode, employmentType, payRate: Number(payRate), payType, startDate }) : [];
  const payChanged = changes.some(c => c.startsWith('pay '));

  const preview = useMemo(() => (roleCode && payRate ? getOfferLetterHtml(roleCode, employmentType, { full_name: staff.full_name, pay_rate: Number(payRate) }, {
    startDateStr: startDate ? format(parseLocalDate(startDate), 'MMMM d, yyyy') : 'a mutually agreed date',
    acceptByStr: signBy ? format(parseLocalDate(signBy), 'MMMM d, yyyy') : '',
  }, staff.preferred_language === 'es' ? 'es' : 'en') : null), [roleCode, payRate, employmentType, startDate, signBy, staff.full_name, staff.preferred_language]);

  const canSend = !busy && staff.email && role?.offerLetters?.[employmentType] && Number(payRate) > 0 && reason.trim();

  const send = async () => {
    setBusy(true); setError('');
    try {
      const res = await reviseOfferLetter(staff, {
        roleCode, employmentType, payRate, startDate, signBy, reason,
        officeName: profile?.full_name, officeRole: profile?.role,
      });
      onDone(res);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const label = (t) => <label className="form-label">{t}</label>;

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{signed ? 'Revise & resend offer letter' : 'Edit & resend offer letter'}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{staff.full_name}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 14 }}>
            {signed
              ? <>A new letter is sent for {staff.first_name || 'them'} to sign. The signed original stays in the personnel file as the record of what was first agreed.</>
              : <>The letter waiting for signature is replaced. The link {staff.first_name || 'they'} already has opens the new version, and a fresh email goes out.</>}
          </div>

          {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
          {!staff.email && <div className="form-error" style={{ marginBottom: 12 }}>No email on file — add one before sending.</div>}

          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-group">
              {label('Employment')}
              <select className="form-select" value={employmentType} onChange={e => setEmploymentType(e.target.value)}>
                <option value="employee">W2 employee</option>
                <option value="contractor">1099 contractor</option>
              </select>
            </div>
            <div className="form-group">
              {label('Role')}
              <select className="form-select" value={roleCode} onChange={e => setRoleCode(e.target.value)}>
                <option value="">Select…</option>
                {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              {label(payType === 'weekly' ? 'Weekly service fee ($)' : 'Pay rate ($ / hour)')}
              <input className="form-input" type="number" min="0" step="0.01" value={payRate} onChange={e => setPayRate(e.target.value)} />
            </div>
            <div className="form-group">
              {label('Start date')}
              <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="form-group">
              {label('Sign by')}
              <input className="form-input" type="date" value={signBy} onChange={e => setSignBy(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            {label('Reason for the change *')}
            <textarea className="form-textarea" rows={2} value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Pay raised after 90-day review" />
          </div>

          <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
            {changes.length ? <>Changes: <b>{changes.join('; ')}</b></> : 'No change to pay, role, W2/1099 or start date — the letter is re-sent as is.'}
            {payChanged && (
              <div style={{ marginTop: 6, color: 'var(--warn)' }}>
                The new rate ({fmtPay(payRate, payType)}) is saved now and used by payroll from the next run, whether or not the letter has been signed.
              </div>
            )}
          </div>

          {preview && (
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowPreview(v => !v)}>{showPreview ? '▾ Hide letter' : '▸ Preview letter'}</button>
              {showPreview && (
                <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: 'var(--text)', lineHeight: 1.6, background: 'var(--bg)' }}
                  dangerouslySetInnerHTML={{ __html: preview }} />
              )}
            </div>
          )}
          {roleCode && !role?.offerLetters?.[employmentType] && (
            <div className="form-error" style={{ marginTop: 12 }}>There is no {TRACK_LABEL[employmentType]} offer letter for {role?.label || 'this role'}.</div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={!canSend}>
            {busy ? <span className="loading-spinner" /> : null} Send to {staff.first_name || 'them'}
          </button>
        </div>
      </div>
    </div>
  );
}
